// 接受邀请：链接形如 /invite#token=...。令牌放在片段里，不进服务器日志和 Referer；读到后立刻从地址栏去掉。

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { describeError } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { roleLabel } from "@/features/org/BindingsDialog";
import { zh } from "@/i18n/zh";

type Preview = {
  org_name: string;
  email: string | null;
  phone: string | null;
  display_name: string | null;
  role_name: string | null;
  expires_at: string;
};

const tokenFromHash = () => new URLSearchParams(location.hash.slice(1)).get("token") ?? "";

export function InvitePage() {
  const qc = useQueryClient();
  const [token, setToken] = useState(tokenFromHash);
  const preview = useQuery({
    queryKey: ["invite-preview", token],
    enabled: !!token,
    retry: false,
    queryFn: () => api<Preview>("/v1/invites/preview", { method: "POST", auth: false, body: { token } }),
  });
  const [name, setName] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const accept = useMutation({
    mutationFn: () =>
      api("/v1/invites/accept", {
        method: "POST",
        auth: false,
        body: { token, display_name: (name ?? preview.data?.display_name ?? "").trim(), password },
      }),
    onError: (e) => setError(describeError(e)),
  });
  const resetAccept = accept.reset;
  // 停在这个页面时再打开一个链接只改片段、不重新加载：要自己读，还要丢掉上一个邀请的结果
  useEffect(() => {
    const sync = () => {
      if (!location.hash) return;
      const next = tokenFromHash();
      history.replaceState(null, "", location.pathname);
      if (next) {
        setToken(next);
        resetAccept();
        setError(null);
        // 同一个链接可能已经用掉了：不能拿缓存的预览当还有效
        void qc.invalidateQueries({ queryKey: ["invite-preview"] });
      }
    };
    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, [qc, resetAccept]);

  function body() {
    if (!token) return <p role="alert" className="text-sm text-destructive">{zh.invite.noToken}</p>;
    if (preview.isPending) return <p className="text-sm text-muted-foreground">{zh.common.loading}</p>;
    if (preview.isError) return <p role="alert" className="text-sm text-destructive">{describeError(preview.error)}</p>;
    if (accept.isSuccess) {
      return (
        <div className="grid gap-4">
          <p role="status" className="text-sm">{zh.invite.done}</p>
          <Link className="text-sm underline" to="/login">{zh.invite.toLogin}</Link>
        </div>
      );
    }
    const p = preview.data;
    return (
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (password !== confirm) {
            setError(zh.password.mismatch);
            return;
          }
          accept.mutate();
        }}
      >
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">{zh.invite.org}</dt>
          <dd data-testid="invite-org">{p.org_name}</dd>
          <dt className="text-muted-foreground">{zh.invite.account}</dt>
          <dd data-testid="invite-account">{p.email ?? p.phone}</dd>
          {p.role_name ? (
            <>
              <dt className="text-muted-foreground">{zh.users.role}</dt>
              <dd>{roleLabel(p.role_name)}</dd>
            </>
          ) : null}
        </dl>
        <Field label={zh.users.name}>
          <Input name="display_name" value={name ?? p.display_name ?? ""} onChange={(e) => setName(e.target.value)} maxLength={64} required />
        </Field>
        <Field label={zh.password.next} hint={zh.users.passwordHint}>
          <Input type="password" name="new-password" autoComplete="new-password" minLength={10} maxLength={128} value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        <Field label={zh.password.confirm}>
          <Input type="password" name="confirm-password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </Field>
        {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
        <Button type="submit" disabled={accept.isPending}>{zh.invite.submit}</Button>
      </form>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardBody>
          <h1 className="mb-1 text-lg font-semibold">{zh.app.name}</h1>
          <p className="mb-5 text-sm text-muted-foreground">{zh.invite.title}</p>
          {body()}
        </CardBody>
      </Card>
    </div>
  );
}
